import type { Board } from "./board";
import { DamageType } from "./damageType";
import { type Direction, directionFromVector } from "./direction";
import { ParticleEffect } from "./effects/particleEffect";
import { isPointsToDirection } from "./interfaces";
import type { ParticleType } from "./particleType";
import { PhysicalThing } from "./physicalThing";
import type { Thing } from "./thing";
import { XY } from "./xy";

const NO_BLEED_TYPES = [DamageType.BUBBLE, DamageType.FIRE, DamageType.ELECTRIC, DamageType.TOXIC_GAS, DamageType.ICE];

/**
 * Standard "organic bleed" effect — emits directional blood particles.
 * Skips non-physical damage (bubble, fire, electric, gas).
 * Resolves hit direction from projectile travel dir or source position.
 *
 * Mob subclasses: override getBloodParticleType() instead of calling this directly.
 * Non-Mob entities: call from applyDamage() when damage > 0.
 */
export function emitBloodEffect(
	board: Board,
	xy: XY,
	bloodType: ParticleType,
	damageType: DamageType | null,
	source: Thing | null,
): void {
	if (damageType !== null && NO_BLEED_TYPES.includes(damageType)) return;

	let hitDirection: Direction | undefined;
	if (isPointsToDirection(source)) {
		const dir = source.getDirection();
		if (dir !== null) hitDirection = dir;
	}
	if (hitDirection === undefined && source instanceof PhysicalThing) {
		const delta = xy.subtract(source.xy);
		hitDirection = directionFromVector(new XY(Math.sign(delta.x), Math.sign(delta.y))) ?? undefined;
	}
	board.addEffect(new ParticleEffect(xy, bloodType, 6, hitDirection));
}
